import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import courses from '../../data/courses'

/**
 * CourseSchema Component
 * Generates and injects JSON-LD Course schema markup for SEO
 * Automatically detects the current route and applies the appropriate course schema
 */
const CourseSchema = () => {
  const location = useLocation()

  // Convert UGX fee to USD (approximate conversion rate: 1 USD = 3,800 UGX)
  const convertToUSD = (ugxFee) => {
    const numericFee = parseInt(ugxFee.replace(/[^0-9]/g, ''))
    const usdAmount = (numericFee / 3800).toFixed(2)
    return usdAmount
  }

  // Parse duration to ISO 8601 format
  const parseISO8601Duration = (duration) => {
    const text = String(duration || '')
    const amount = parseFloat(text.match(/(\d+(?:\.\d+)?)/)?.[1] || 0)
    if (/month/i.test(text)) return `P${amount}M`
    if (/week/i.test(text)) return `P${amount}W`
    return `P${amount}M`
  }

  // Generate schema for all courses (for landing page)
  const generateAllCoursesSchema = () => {
    return courses.map((course) => ({
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: course.title,
      description: course.description,
      provider: {
        '@type': 'Organization',
        name: 'Synergy Multimedia Academy',
        url: 'https://www.synergymultimedia.co.ug',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Wakiso District',
          addressLocality: 'Kampala',
          addressRegion: 'Central Region',
          addressCountry: 'UG',
        },
        logo: 'https://www.synergymultimedia.co.ug/Logos/SMA%20LOGO%20%20DARK%20MODE.png',
        sameAs: [
          'https://www.facebook.com/synergymultimedia',
          'https://www.instagram.com/synergymultimedia',
        ],
      },
      educationalLevel: course.level,
      courseCode: course.code,
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'onsite',
        duration: parseISO8601Duration(course.duration),
        inLanguage: 'en',
        location: {
          '@type': 'Place',
          name: 'Synergy Multimedia Academy Campus',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Kampala',
            addressRegion: 'Wakiso',
            addressCountry: 'Uganda',
          },
        },
      },
      offers: {
        '@type': 'Offer',
        price: convertToUSD(course.fee),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        validFrom: '2026-01-01',
      },
      audience: {
        '@type': 'EducationalAudience',
        educationalRole: 'student',
      },
      teaches: course.category,
      timeRequired: parseISO8601Duration(course.duration),
    }))
  }

  // Find course schema for specific course page
  const findCourseSchema = () => {
    const currentPath = location.pathname
    const course = courses.find((c) => c.url === currentPath)

    if (!course) return null

    return {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: course.title,
      description: course.description,
      provider: {
        '@type': 'Organization',
        name: 'Synergy Multimedia Academy',
        url: 'https://www.synergymultimedia.co.ug',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Wakiso District',
          addressLocality: 'Kampala',
          addressRegion: 'Central Region',
          addressCountry: 'UG',
        },
        logo: 'https://www.synergymultimedia.co.ug/Logos/SMA%20LOGO%20%20DARK%20MODE.png',
        sameAs: [
          'https://www.facebook.com/synergymultimedia',
          'https://www.instagram.com/synergymultimedia',
        ],
      },
      educationalLevel: course.level,
      courseCode: course.code,
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'onsite',
        duration: parseISO8601Duration(course.duration),
        inLanguage: 'en',
        instructor: {
          '@type': 'Person',
          name: 'Professional Industry Trainers',
        },
        location: {
          '@type': 'Place',
          name: 'Synergy Multimedia Academy Campus',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Kampala',
            addressRegion: 'Wakiso',
            addressCountry: 'Uganda',
          },
        },
      },
      offers: {
        '@type': 'Offer',
        price: convertToUSD(course.fee),
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        validFrom: '2026-01-01',
        url: `https://www.synergymultimedia.co.ug${course.url}`,
      },
      audience: {
        '@type': 'EducationalAudience',
        educationalRole: 'student',
      },
      teaches: course.category,
      timeRequired: parseISO8601Duration(course.duration),
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '50',
        bestRating: '5',
      },
    }
  }

  // Determine which schema to use based on route
  const getSchemaForCurrentRoute = () => {
    // Landing page gets all courses
    if (location.pathname === '/explore') {
      return generateAllCoursesSchema()
    }

    // Individual course pages get specific schema
    const courseSchema = findCourseSchema()
    return courseSchema ? [courseSchema] : null
  }

  const schemas = getSchemaForCurrentRoute()

  if (!schemas) return null

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  )
}

export default CourseSchema
